import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { AuthShell } from "@/components/auth/auth-shell";
import { AuthLoading } from "@/components/auth/auth-loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMe } from "@/hooks/use-auth";
import { api } from "@/lib/api";
import type { ChangePasswordRequest } from "@/lib/api-types";
import { authErrorMessage } from "@/lib/auth-errors";

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const { data: me, isLoading: meLoading } = useMe();

  const next = new URLSearchParams(loc.search).get("next") || "/app";

  const [current, setCurrent] = useState("");
  const [next_, setNext_] = useState("");
  const [confirm, setConfirm] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (body: ChangePasswordRequest) =>
      api.post<{ ok: true }>("/auth/change-password", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auth", "workspace-me"] });
    },
  });

  if (meLoading) return <AuthLoading />;
  if (!me) return <Navigate to="/login" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setValidationError(null);
    if (next_.length < 8) {
      setValidationError("New password must be at least 8 characters.");
      return;
    }
    if (next_ !== confirm) {
      setValidationError("Passwords don't match.");
      return;
    }
    if (next_ === current) {
      setValidationError("New password must be different from the current one.");
      return;
    }
    try {
      await mut.mutateAsync({ currentPassword: current, newPassword: next_ });
      navigate(next, { replace: true });
    } catch {
      /* surfaced via mut.error */
    }
  }

  const err = validationError ?? authErrorMessage(mut.error);
  const canSubmit =
    current.length > 0 &&
    next_.length > 0 &&
    confirm.length > 0 &&
    !mut.isPending;

  return (
    <AuthShell>
      <div className="animate-fade-in-up rounded-2xl border border-border bg-card/70 p-6 shadow-card backdrop-blur md:p-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand-soft px-2.5 py-0.5 text-xs uppercase tracking-widest text-foreground">
          Security update
        </div>
        <h1 className="mt-4 font-display text-3xl font-black tracking-tight">
          Set a new password
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Change your temporary password before continuing.
        </p>

        <form onSubmit={onSubmit} className="mt-7 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cp-current">Current password</Label>
            <Input
              id="cp-current"
              type="password"
              autoComplete="current-password"
              autoFocus
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              disabled={mut.isPending}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-new">New password</Label>
            <Input
              id="cp-new"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={next_}
              onChange={(e) => setNext_(e.target.value)}
              disabled={mut.isPending}
            />
            <p className="text-xs text-muted-foreground">At least 8 characters.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input
              id="cp-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={mut.isPending}
            />
          </div>

          {err && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={!canSubmit}
          >
            {mut.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Updating…
              </>
            ) : (
              "Update password"
            )}
          </Button>
        </form>
      </div>
    </AuthShell>
  );
}
